import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { Source } from "@tunic/shared";
import { useInbox, useSourceActions } from "@/api/sources";
import { useProjects, useUpdateProject, useUpdateTask } from "@/api/projects";
import { useUndoToast } from "@/hooks/useUndoToast";
import { Page, PageHeader } from "@/components/shell/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SourceRow } from "@/components/sources/SourcesPanel";
import { TaskRow } from "@/components/tasks/TaskList";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_app/inbox")({ component: InboxPage });

function InboxPage() {
  const inbox = useInbox();
  const empty = inbox.data && inbox.data.sources.length === 0 && inbox.data.tasks.length === 0 && inbox.data.projects.length === 0;
  return (
    <>
      <PageHeader title="Inbox" subtitle="Things the assistant was not sure about. One decision each." />
      <Page className="space-y-6">
        {empty && <EmptyState title="Nothing to review" hint="Drops the assistant could not place, and AI-created tasks worth a glance, land here." />}
        {!!inbox.data?.sources.length && (
          <section>
            <h2 className="mb-2 text-sm font-semibold">Where does this belong? ({inbox.data.sources.length})</h2>
            <ul className="divide-y rounded-lg border bg-card">
              {inbox.data.sources.map((s) => (
                <SourceRow key={s.id} source={s}>
                  <ResolveBar source={s} />
                </SourceRow>
              ))}
            </ul>
          </section>
        )}
        {!!inbox.data?.projects.length && (
          <section>
            <h2 className="mb-2 text-sm font-semibold">Projects the assistant created ({inbox.data.projects.length})</h2>
            <ul className="divide-y rounded-lg border bg-card">
              {inbox.data.projects.map((p) => (
                <ProjectReviewRow key={p.id} id={p.id} name={p.name} slug={p.slug} summary={p.summary} />
              ))}
            </ul>
          </section>
        )}
        {!!inbox.data?.tasks.length && (
          <section>
            <h2 className="mb-2 text-sm font-semibold">AI-drafted tasks to check ({inbox.data.tasks.length})</h2>
            <ul className="rounded-lg border bg-card">
              {inbox.data.tasks.map((t) => (
                <TaskReviewRow key={t.id} taskId={t.id} projectId={t.project_id}>
                  <TaskRow task={t} projectId={t.project_id} />
                </TaskReviewRow>
              ))}
            </ul>
          </section>
        )}
      </Page>
    </>
  );
}

function ResolveBar({ source: s }: { source: Source }) {
  const projects = useProjects({});
  const { resolve } = useSourceActions();
  const [pick, setPick] = useState<string>(s.proposal?.project_id ?? "");
  const p = s.proposal;
  return (
    <div className="mt-2 space-y-2">
      {p && (
        <p className="text-xs">
          <span className="font-medium">Assistant:</span> {p.rationale}
          {p.new_project_name && (
            <>
              {" "}
              Suggests new project <span className="font-medium">"{p.new_project_name}"</span>.
            </>
          )}
          {(p.candidate_step_count > 0 || p.candidate_task_count > 0) && (
            <span className="text-muted-foreground">
              {" "}
              Would add {p.candidate_step_count} steps, {p.candidate_task_count} tasks.
            </span>
          )}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger className="h-8 w-56">
            <SelectValue placeholder="Choose a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.data?.map((pr) => (
              <SelectItem key={pr.id} value={pr.id}>
                {pr.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" disabled={!pick || resolve.isPending} onClick={() => resolve.mutate({ id: s.id, input: { action: "assign", project_id: pick } })}>
          File here
        </Button>
        <Button size="sm" variant="outline" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: s.id, input: { action: "create_project" } })}>
          Create {p?.new_project_name ? `"${p.new_project_name}"` : "new project"}
        </Button>
        <Button size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: s.id, input: { action: "discard" } })}>
          Discard
        </Button>
      </div>
    </div>
  );
}

function ProjectReviewRow({ id, name, slug, summary }: { id: string; name: string; slug: string; summary: string }) {
  const update = useUpdateProject(id);
  const undoToast = useUndoToast();
  return (
    <li className="flex items-center gap-3 px-3 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <Link to="/projects/$projectId" params={{ projectId: slug }} className="font-medium hover:underline">
          {name}
        </Link>
        <div className="truncate text-xs text-muted-foreground">{summary}</div>
      </div>
      <Button size="sm" onClick={() => undoToast.run(update.mutateAsync({ needs_review: false, status: "active" }), `Kept ${name}`)}>
        Keep
      </Button>
      <Button size="sm" variant="ghost" className="text-danger" onClick={() => undoToast.run(update.mutateAsync({ status: "dropped" }), `Dropped ${name}`)}>
        Drop
      </Button>
    </li>
  );
}

function TaskReviewRow({ taskId, projectId, children }: { taskId: string; projectId: string; children: React.ReactNode }) {
  const update = useUpdateTask(projectId);
  const undoToast = useUndoToast();
  return (
    <div className="flex items-center gap-2 border-b last:border-b-0">
      <ul className="min-w-0 flex-1">{children}</ul>
      <Button size="sm" variant="outline" className="mr-2 h-7 text-xs" onClick={() => undoToast.run(update.mutateAsync({ id: taskId, patch: { needs_review: false } }), "Looks good")}>
        Looks good
      </Button>
    </div>
  );
}
